import React, { useState, useCallback, useMemo, type ChangeEvent, type KeyboardEvent } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { calculateProfileCompletion } from '../../services/profileService';
import { trackEditorPickClicked, trackAffiliateClick } from '../../services/eventTrackingService';
import { detectUserRegion } from '../../config/affiliateConfig';
import AddressForm from '../checkout/AddressForm';
import ProfileProgress from './ProfileProgress';
import SkinReportCard from './SkinReportCard';
import './ProfilePanel.css';
import type { MasterProfile, LogisticsSection } from '../../types/profile';
import type { WishlistItem, AffiliateSource } from '../../types/product';
import { INHOUSE_SELLING_ENABLED, SAMPLE_FEATURE_ENABLED } from '../../config/adminConfig';
import { cleanProductTitle } from '../../utils/productTitle';
import { openProductUrl } from '../../utils/openProductUrl';

// Re-export for backwards compatibility
export type { MasterProfile };

/**
 * Get display name for affiliate source
 * Used for badge and button text
 */
const getAffiliateDisplayName = (source?: AffiliateSource, brand?: string): string => {
  const sourceMap: Record<AffiliateSource, string> = {
    amazon: 'Amazon',
    coupang: 'Coupang',
    iherb: 'iHerb',
    yesstyle: 'YesStyle',
    direct: brand || 'Shop',
    inhouse: 'Heseda',
    partner: brand || 'Partner',
  };
  return source ? sourceMap[source] : (brand || 'Shop');
};

// =============================================================================
// Type Definitions
// =============================================================================

/** Profile diff entry showing old/new values */
export interface ProfileDiff {
  [path: string]: {
    old?: unknown;
    new?: unknown;
  };
}

/** Profile conflict entry */
export interface ProfileConflict {
  path?: string;
  stored?: unknown;
  existing?: unknown;
  incoming?: unknown;
  proposed?: unknown;
  confidence?: number;
  evidence?: string;
}

/** Recent change entry */
export interface RecentChange {
  path: string;
  newVal?: unknown;
}

/** Editor pick/product recommendation */
export interface EditorPick {
  id: string;
  name: string;
  title?: string; // Fallback from catalog data
  brand?: string;
  price?: number | string;
  priceUSD?: number;
  image?: string;
  url?: string;
  confidence?: number;
  reason?: string;
  summary?: string;
  tags?: string[];
  reasons?: string[];
  isPaidPlacement?: boolean;
  
  // Affiliate tracking fields
  /** Affiliate URL for monetization (Amazon, Coupang, etc.) */
  affiliateUrl?: string;
  /** Source of affiliate link */
  affiliateSource?: AffiliateSource;
  /** Internal tracking ID for click attribution */
  trackingId?: string;
  /** Cost-per-click bid for sponsored products */
  cpcBid?: number;
  /** Revenue share percentage (e.g., 0.07 = 7%) */
  revenueShare?: number;
}

/** Cart item structure */
export interface CartItem {
  id: string;
  name?: string;
  title?: string;
  price?: number | string;
  priceUSD?: number;
  image?: string;
  brand?: string;
  quantity?: number;
}

/** Firebase user type */
export interface FirebaseUser {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}

/** Props for ProfilePanel component */
export interface ProfilePanelProps {
  masterProfile: Partial<MasterProfile>;
  recentChanges?: RecentChange[];
  diff?: ProfileDiff;
  conflicts?: ProfileConflict[];
  coppaBlocked?: boolean;
  isExtracting?: boolean;
  isSaving?: boolean;
  profileError?: string | null;
  onUndo?: (path: string) => void;
  editorPicks?: EditorPick[];
  isEditorPicksLoading?: boolean;
  /** Whether editor picks are from affiliate sources (Amazon) */
  isEditorPicksAffiliate?: boolean;
  onRefreshEditorPicks?: () => void;
  autoSave?: boolean;
  onUpdateProfile?: (profile: MasterProfile) => void;
  language?: string;
  showAddressFormForSample?: boolean;
  onCancelAddressForm?: () => void;
  user?: FirebaseUser | null;
  hideHeader?: boolean;
  cartItems?: CartItem[];
  onAddToCart?: (item: CartItem) => void;
  onPhotoUpload?: (file: File) => void;
  // Wishlist props
  wishlistItems?: WishlistItem[];
  onWishlistToggle?: (product: EditorPick) => void;
  isInWishlist?: (productId: string) => boolean;
  /** Callback to open product detail panel (instead of external link) */
  onViewDetails?: (pick: EditorPick) => void;
  /** Callback to ask AI about a product (pre-fills chat input) */
  onAskAI?: (productName: string) => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format timestamp to pretty time string
 */
const prettyTime = (value: unknown): string => {
  if (!value) return '';
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    // Firebase Timestamp
    const firebaseTs = value as { toDate: () => Date };
    return firebaseTs.toDate().toLocaleString();
  }
  if (value instanceof Date) {
    return value.toLocaleString();
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleString();
  }
  return String(value);
};

/**
 * Ensure all expected sections exist in profile
 */
const normalizeProfile = (profile: MasterProfile | null | undefined): MasterProfile => {
  return {
    identity: profile?.identity || {},
    consent: profile?.consent || {},
    skin: profile?.skin || {},
    logistics: profile?.logistics || {},
    history: profile?.history || {},
    segment: profile?.segment || {},
    meta: profile?.meta || {},
  };
};

/**
 * Render JSON with syntax highlighting
 */
const renderJson = (value: unknown): React.ReactNode => {
  if (value === undefined || value === null) return <span className="json-null">null</span>;
  if (typeof value === 'boolean') return <span className="json-boolean">{String(value)}</span>;
  if (typeof value === 'number') return <span className="json-number">{value}</span>;
  if (typeof value === 'string') return <span className="json-string">"{value}"</span>;
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="json-array">[]</span>;
    return (
      <span className="json-array">
        [{value.map((item, i) => (
          <React.Fragment key={i}>
            {i > 0 && ', '}
            {renderJson(item)}
          </React.Fragment>
        ))}]
      </span>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span className="json-object">{'{}'}</span>;
    return (
      <span className="json-object">
        {'{'}
        {entries.map(([key, val], i) => (
          <React.Fragment key={key}>
            {i > 0 && ', '}
            <span className="json-key">"{key}"</span>: {renderJson(val)}
          </React.Fragment>
        ))}
        {'}'}
      </span>
    );
  }
  return <span>{String(value)}</span>;
};

/**
 * Check if a field path was recently updated
 * @param path - Field path to check (e.g., 'skin.type', 'skin.concerns')
 * @param recentChanges - Array of recent changes
 * @param diff - Diff object with path keys
 * @returns true if field was recently updated
 */
const isRecentlyUpdated = (
  path: string,
  recentChanges: RecentChange[] = [],
  diff: ProfileDiff = {}
): boolean => {
  // Guard against null/undefined
  const safeRecentChanges = recentChanges || [];
  const safeDiff = diff || {};
  
  // Match logic:
  // - Exact match: 'skin.type' === 'skin.type'
  // - Child match: path='skin' matches change='skin.type' (change is child of path)
  // - Parent match: path='skin.type' matches change='skin' (path is child of change)
  // But NOT: 'skin.concerns' should NOT match 'skin.type' (siblings)
  
  const matchesPath = (changePath: string, targetPath: string): boolean => {
    if (changePath === targetPath) return true;
    // changePath is a child of targetPath: skin.type starts with skin.
    if (changePath.startsWith(`${targetPath}.`)) return true;
    // targetPath is a child of changePath: skin.type starts with skin.
    if (targetPath.startsWith(`${changePath}.`)) return true;
    return false;
  };
  
  // Check in recentChanges array
  const inRecentChanges = safeRecentChanges.some(change => 
    matchesPath(change.path, path)
  );
  
  // Check in diff object
  const inDiff = Object.keys(safeDiff).some(diffPath => 
    matchesPath(diffPath, path)
  );
  
  return inRecentChanges || inDiff;
};

/**
 * Badge component for recently updated fields
 * Now positioned absolutely on the right side of the card
 */
const UpdatedBadge: React.FC = () => (
  <span className="profile-card__badge profile-card__badge--updated">NEW</span>
);

// =============================================================================
// ProfilePanel Component
// =============================================================================

const ProfilePanel: React.FC<ProfilePanelProps> = ({
  masterProfile,
  recentChanges = [],
  diff = {},
  conflicts = [],
  coppaBlocked = false,
  isExtracting = false,
  isSaving = false,
  profileError,
  onUndo,
  editorPicks = [],
  isEditorPicksLoading = false,
  isEditorPicksAffiliate = false,
  onRefreshEditorPicks = () => {},
  autoSave = false,
  onUpdateProfile = () => {},
  language = 'ko',
  showAddressFormForSample = false,
  onCancelAddressForm = () => {},
  user,
  hideHeader = false,
  cartItems = [],
  onAddToCart = () => {},
  onPhotoUpload,
  wishlistItems = [],
  onWishlistToggle,
  isInWishlist,
  onViewDetails,
  onAskAI,
}) => {
  // i18n
  const { t } = useTranslation('common');
  const { t: tProfile } = useTranslation('profile');

  // Local state
  const [isAddressFormOpen, setIsAddressFormOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [editingConcerns, setEditingConcerns] = useState(false);
  const [concernsInput, setConcernsInput] = useState('');
  const [showAiDetails, setShowAiDetails] = useState(false);
  const [showReportCard, setShowReportCard] = useState(false);

  // Normalize profile to ensure all sections exist
  const profile = useMemo(() => normalizeProfile(masterProfile), [masterProfile]);

  // Calculate profile completion
  // Cast to MasterProfile type expected by calculateProfileCompletion (local types are more permissive)
  const completion = useMemo(() => calculateProfileCompletion(profile as unknown as import('../../types/profile').MasterProfile), [profile]);

  // Field status helpers - determines if fields are filled
  const hasName = Boolean(profile.identity?.name);
  const hasSkinType = Boolean(profile.skin?.type);
  const hasConcerns = Boolean(profile.skin?.concerns && profile.skin.concerns.length > 0);
  const hasAllergies = Boolean(
    (profile.skin?.allergies && profile.skin.allergies.length > 0) ||
    (profile.skin?.disliked && profile.skin.disliked.length > 0)
  );
  const hasAddress = Boolean(profile.logistics?.address1);
  const hasBudget = Boolean(profile.skin?.budgetMonthlyUSD != null);
  const hasBrands = Boolean(profile.skin?.preferredBrands && profile.skin.preferredBrands.length > 0);
  const hasGoals = Boolean(profile.skin?.routineGoals && profile.skin.routineGoals.length > 0);
  const hasAgeGroup = Boolean(profile.identity?.ageGroup);
  const hasCurrentRoutine = Boolean(profile.skin?.currentProducts && profile.skin.currentProducts.length > 0);
  const hasAiAnalysis = Boolean(profile.skin?.aiAnalysis?.summary);

  // Handle starting inline edit
  const handleStartEdit = useCallback((field: string, currentValue: unknown) => {
    setEditingField(field);
    setEditValue(currentValue !== undefined && currentValue !== null ? String(currentValue) : '');
  }, []);

  // Handle canceling inline edit
  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue('');
  }, []);

  // Handle saving inline edit
  const handleSaveEdit = useCallback((field: string, section: string, isNumber: boolean = false) => {
    if (!editingField) return;

    const newProfile = { ...profile };
    const sectionKey = section as keyof MasterProfile;
    const sectionData = { ...(newProfile[sectionKey] as Record<string, unknown> || {}) };
    
    if (isNumber) {
      const numValue = parseFloat(editValue);
      if (!isNaN(numValue)) {
        sectionData[field] = numValue;
      }
    } else {
      sectionData[field] = editValue;
    }
    
    (newProfile[sectionKey] as Record<string, unknown>) = sectionData;
    onUpdateProfile(newProfile);
    setEditingField(null);
    setEditValue('');
  }, [editingField, editValue, profile, onUpdateProfile]);

  // Handle key press for inline editing
  const handleKeyPress = useCallback((e: KeyboardEvent<HTMLInputElement>, field: string, section: string, isNumber: boolean = false) => {
    if (e.key === 'Enter') {
      handleSaveEdit(field, section, isNumber);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  }, [handleSaveEdit, handleCancelEdit]);

  // Handle concerns editing
  const handleStartConcernsEdit = useCallback(() => {
    setEditingConcerns(true);
    setConcernsInput((profile.skin?.concerns || []).join(', '));
  }, [profile.skin?.concerns]);

  const handleSaveConcerns = useCallback(() => {
    const newConcerns = concernsInput
      .split(',')
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    
    const newProfile = {
      ...profile,
      skin: {
        ...profile.skin,
        concerns: newConcerns,
      },
    };
    onUpdateProfile(newProfile);
    setEditingConcerns(false);
    setConcernsInput('');
  }, [concernsInput, profile, onUpdateProfile]);

  const handleCancelConcernsEdit = useCallback(() => {
    setEditingConcerns(false);
    setConcernsInput('');
  }, []);

  // Handle array field editing (brands, goals, allergies)
  const handleSaveArrayField = useCallback((field: string, section: string, inputValue: string) => {
    const newArray = inputValue
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
    
    const newProfile = { ...profile };
    const sectionKey = section as keyof MasterProfile;
    const sectionData = { ...(newProfile[sectionKey] as Record<string, unknown> || {}) };
    sectionData[field] = newArray;
    (newProfile[sectionKey] as Record<string, unknown>) = sectionData;
    
    onUpdateProfile(newProfile);
    setEditingField(null);
    setEditValue('');
  }, [profile, onUpdateProfile]);

  // Handle address form save
  const handleSaveAddress = useCallback((address: LogisticsSection) => {
    const newProfile = {
      ...profile,
      logistics: {
        ...profile.logistics,
        ...address,
      },
    };
    onUpdateProfile(newProfile);
    setIsAddressFormOpen(false);
    if (showAddressFormForSample) {
      onCancelAddressForm();
    }
  }, [profile, onUpdateProfile, showAddressFormForSample, onCancelAddressForm]);

  // Handle address form cancel
  const handleCancelAddressForm = useCallback(() => {
    setIsAddressFormOpen(false);
    if (showAddressFormForSample) {
      onCancelAddressForm();
    }
  }, [showAddressFormForSample, onCancelAddressForm]);

  // Get skin type display value
  const getSkinTypeDisplay = (type?: string): string => {
    if (!type) return t('labels.notSet');
    const key = type.toLowerCase();
    // Map 'combo' to 'combination'
    let normalizedKey = key === 'combo' ? 'combination' : key;
    // Map Korean stored values back to English keys
    const koToEnMap: Record<string, string> = {
      '지성': 'oily',
      '건성': 'dry',
      '중성': 'normal',
      '복합성': 'combination',
      '민감성': 'sensitive',
    };
    if (koToEnMap[type]) normalizedKey = koToEnMap[type];
    const translationKey = `skinTypes.${normalizedKey}`;
    const translated = t(translationKey);
    // If translation key not found, return original
    return translated === translationKey ? type : translated;
  };

  // Get concern display value (translate English to Korean if needed)
  const getConcernDisplay = (concern: string): string => {
    const lowerConcern = concern.toLowerCase().trim();
    // Convert to camelCase key (e.g., 'dark spots' -> 'darkSpots')
    const key = lowerConcern
      .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
      .replace(/\s+/g, '');
    const translationKey = `skinConcerns.${key}`;
    const translated = t(translationKey);
    // If translation key not found, return original
    return translated === translationKey ? concern : translated;
  };

  // Get ingredient/allergy display value (translate if key exists)
  const getIngredientDisplay = (item: string): string => {
    const lowerItem = item.toLowerCase().trim();
    const key = lowerItem
      .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
      .replace(/\s+/g, '');
    const translationKey = `ingredientConcerns.${key}`;
    const translated = t(translationKey);
    return translated === translationKey ? item : translated;
  };

  return (
    <aside className="profile-panel">
      {/* Header */}
      {!hideHeader && (
        <header className="profile-panel__header">
          <h2>{tProfile('masterProfile')}</h2>
          <div className="profile-panel__header-actions">
            {isSaving && <span className="profile-panel__saving">💾 Saving...</span>}
            {isExtracting && <span className="profile-panel__extracting">🔍 Analyzing...</span>}
          </div>
        </header>
      )}

      {/* COPPA Blocked Warning */}
      {coppaBlocked && (
        <div className="profile-panel__coppa-warning">
          ⚠️ {tProfile('coppa.blocked')}
        </div>
      )}

      {/* Profile Error */}
      {profileError && (
        <div className="profile-panel__error">
          ❌ {profileError}
        </div>
      )}

      {/* Profile Progress */}
      <ProfileProgress completion={completion} language={language} />

      {/* Profile Completion Checklist — show all missing fields */}
      {completion < 100 && (() => {
        const missingFields: string[] = [];
        if (!hasSkinType) missingFields.push('skinType');
        if (!hasConcerns) missingFields.push('concerns');
        if (!hasAgeGroup) missingFields.push('ageGroup');
        if (!hasAllergies) missingFields.push('allergies');
        if (!hasGoals) missingFields.push('goals');
        if (!hasBudget) missingFields.push('budget');
        if (missingFields.length === 0) return null;
        return (
          <div className="profile-panel__nudge" role="status">
            {missingFields.map((field) => (
              <div key={field} style={{ marginBottom: '2px' }}>
                <span>{tProfile(`nudge.${field}`)}</span>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Profile Cards */}
      <div className="profile-panel__cards">
        {/* Name Card */}
        <div className={`profile-card ${!hasName ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('identity.name', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">
              {profile.identity?.photoURL ? (
                <img 
                  src={profile.identity.photoURL} 
                  alt="Profile" 
                  style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }}
                  referrerPolicy="no-referrer"
                />
              ) : (
                '👤'
              )}
            </span>
            <span className="profile-card__title">{tProfile('fields.name')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'name' ? (
              <div className="profile-card__edit">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'name', 'identity')}
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button type="button" className="btn btn--sm btn--primary" onClick={() => handleSaveEdit('name', 'identity')}>
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-card__value profile-card__value--editable"
                onClick={() => handleStartEdit('name', profile.identity?.name)}
              >
                {profile.identity?.name || tProfile('placeholders.tellMeYourName')}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Email Card - Always visible, editable, highlighted if empty */}
        <div className={`profile-card ${!profile.identity?.email ? 'profile-card--warning' : ''}`}>
          {isRecentlyUpdated('identity.email', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">📧</span>
            <span className="profile-card__title">{tProfile('fields.email')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'email' ? (
              <div className="profile-card__edit">
                <input
                  type="email"
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'email', 'identity')}
                  placeholder={tProfile('placeholders.emailPlaceholder')}
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button type="button" className="btn btn--sm btn--primary" onClick={() => handleSaveEdit('email', 'identity')}>
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-card__value profile-card__value--editable"
                onClick={() => handleStartEdit('email', profile.identity?.email)}
              >
                {profile.identity?.email || tProfile('placeholders.tellMeYourEmail')}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
            {SAMPLE_FEATURE_ENABLED && !profile.identity?.email && (
              <div style={{ 
                fontSize: '0.75rem', 
                color: '#f59e0b', 
                marginTop: '6px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span>📦</span>
                <span>{tProfile('hints.requiredForSampleDelivery')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Skin Type Card - CRITICAL: needs error class when missing */}
        <div className={`profile-card ${!hasSkinType ? 'profile-card--error' : ''}`}>
          {isRecentlyUpdated('skin.type', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">💧</span>
            <span className="profile-card__title">{tProfile('skinType')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'skinType' ? (
              <div className="profile-card__edit">
                <select
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setEditValue(e.target.value)}
                  className="profile-card__select"
                  autoFocus
                >
                  <option value="">{t('actions.select')}</option>
                  <option value="dry">{t('skinTypes.dry')}</option>
                  <option value="oily">{t('skinTypes.oily')}</option>
                  <option value="combination">{t('skinTypes.combination')}</option>
                  <option value="sensitive">{t('skinTypes.sensitive')}</option>
                  <option value="normal">{t('skinTypes.normal')}</option>
                </select>
                <div className="profile-card__edit-actions">
                  <button type="button" className="btn btn--sm btn--primary" onClick={() => handleSaveEdit('type', 'skin')}>
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="profile-card__value profile-card__value--editable"
                onClick={() => handleStartEdit('skinType', profile.skin?.type || '')}
              >
                {getSkinTypeDisplay(profile.skin?.type)}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Skin Concerns Card */}
        <div className={`profile-card ${!hasConcerns ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('skin.concerns', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">🎯</span>
            <span className="profile-card__title">{tProfile('concerns')}</span>
          </div>
          <div className="profile-card__content">
            {editingConcerns ? (
              <div className="profile-card__edit">
                <input
                  type="text"
                  value={concernsInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setConcernsInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveConcerns();
                    if (e.key === 'Escape') handleCancelConcernsEdit();
                  }}
                  placeholder={tProfile('placeholders.separateWithCommas')}
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button type="button" className="btn btn--sm btn--primary" onClick={handleSaveConcerns}>
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelConcernsEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-card__tags profile-card__value--editable"
                onClick={handleStartConcernsEdit}
              >
                {profile.skin?.concerns && profile.skin.concerns.length > 0 ? (
                  profile.skin.concerns.map((concern, idx) => (
                    <span key={idx} className="profile-card__tag">
                      {getConcernDisplay(concern)}
                    </span>
                  ))
                ) : (
                  <span className="profile-card__empty">
                    {tProfile('placeholders.tellMeYourConcerns')}
                  </span>
                )}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Allergies Card - safety critical, near skin info */}
        <div className={`profile-card ${!hasAllergies ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('skin.allergies', recentChanges, diff) && <UpdatedBadge />}
          {hasAllergies && (
            <span className="profile-card__badge profile-card__badge--caution">
              {t('labels.caution')}
            </span>
          )}
          <div className="profile-card__header">
            <span className="profile-card__icon">⚠️</span>
            <span className="profile-card__title">
              {tProfile('allergies')}
            </span>
          </div>
          <div className="profile-card__content">
            {editingField === 'allergies' ? (
              <div className="profile-card__edit">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveArrayField('allergies', 'skin', editValue);
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  placeholder={tProfile('placeholders.separateWithCommas')}
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button 
                    type="button" 
                    className="btn btn--sm btn--primary" 
                    onClick={() => handleSaveArrayField('allergies', 'skin', editValue)}
                  >
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-card__tags profile-card__value--editable"
                onClick={() => handleStartEdit('allergies', (profile.skin?.allergies || []).join(', '))}
              >
                {(profile.skin?.allergies && profile.skin.allergies.length > 0) || (profile.skin?.disliked && profile.skin.disliked.length > 0) ? (
                  <>
                    {(profile.skin?.allergies || []).map((allergy, idx) => (
                      <span key={`a-${idx}`} className="profile-card__tag profile-card__tag--warning">
                        {getIngredientDisplay(allergy)}
                      </span>
                    ))}
                    {(profile.skin?.disliked || []).map((item, idx) => (
                      <span key={`d-${idx}`} className="profile-card__tag profile-card__tag--muted">
                        {getIngredientDisplay(item)}
                      </span>
                    ))}
                  </>
                ) : (
                  <span className="profile-card__empty">
                    {tProfile('placeholders.none')}
                  </span>
                )}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Age Card */}
        <div className={`profile-card ${!hasAgeGroup ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('identity.ageGroup', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">🎂</span>
            <span className="profile-card__title">{tProfile('ageGroup')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'ageGroup' ? (
              <div className="profile-card__edit">
                <select
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setEditValue(e.target.value)}
                  className="profile-card__select"
                  autoFocus
                >
                  <option value="">{t('actions.select')}</option>
                  <option value="teens">{t('ageGroups.teens')}</option>
                  <option value="20s">{t('ageGroups.20s')}</option>
                  <option value="30s">{t('ageGroups.30s')}</option>
                  <option value="40s">{t('ageGroups.40s')}</option>
                  <option value="50s">{t('ageGroups.50s')}</option>
                  <option value="60s+">{t('ageGroups.60sPlus')}</option>
                </select>
                <div className="profile-card__edit-actions">
                  <button type="button" className="btn btn--sm btn--primary" onClick={() => handleSaveEdit('ageGroup', 'identity')}>
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="profile-card__value profile-card__value--editable"
                onClick={() => handleStartEdit('ageGroup', profile.identity?.ageGroup || '')}
              >
                {profile.identity?.ageGroup || tProfile('placeholders.notSet')}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Budget Card - purchase intent signal */}
        <div className={`profile-card ${!hasBudget ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('skin.budgetMonthlyUSD', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">💰</span>
            <span className="profile-card__title">{tProfile('monthlyBudget')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'budgetMonthlyUSD' ? (
              <div className="profile-card__edit">
                <input
                  type="number"
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                  onKeyDown={(e) => handleKeyPress(e, 'budgetMonthlyUSD', 'skin', true)}
                  min="0"
                  step="10"
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button 
                    type="button" 
                    className="btn btn--sm btn--primary" 
                    onClick={() => handleSaveEdit('budgetMonthlyUSD', 'skin', true)}
                  >
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-card__value profile-card__value--editable"
                onClick={() => handleStartEdit('budgetMonthlyUSD', profile.skin?.budgetMonthlyUSD)}
              >
                {profile.skin?.budgetMonthlyUSD != null 
                  ? `$${profile.skin.budgetMonthlyUSD}/mo`
                  : tProfile('placeholders.notSet')}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* AI Skin Analysis Card - summary/insights */}
        {profile.skin?.aiAnalysis && (
          <div className={`profile-card profile-card--ai-analysis ${!hasAiAnalysis ? 'profile-card--empty' : ''}`}>
            {isRecentlyUpdated('skin.aiAnalysis', recentChanges, diff) && <UpdatedBadge />}
            <div className="profile-card__header">
              <span className="profile-card__icon">🤖</span>
              <span className="profile-card__title">{tProfile('aiSkinAnalysis')}</span>
              <button 
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setShowAiDetails(!showAiDetails)}
              >
                {showAiDetails ? '▼' : '▶'}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setShowReportCard(true)}
                title="Share Skin Report Card"
                aria-label="Share Skin Report Card"
              >
                📤
              </button>
            </div>
            <div className="profile-card__content">
              {profile.skin.aiAnalysis.summary && (
                <p className="profile-card__ai-summary">{profile.skin.aiAnalysis.summary}</p>
              )}
              {profile.skin.aiAnalysis.confidence != null && (
                <div className="profile-card__confidence">
                  <span>{tProfile('fields.confidence')}:</span>
                  <span className="profile-card__confidence-value">
                    {Math.round(Number(profile.skin.aiAnalysis.confidence) * 100)}%
                  </span>
                </div>
              )}
              {showAiDetails && (
                <div className="profile-card__ai-details">
                  {profile.skin.aiAnalysis.analysisDate && (
                    <p>
                      <strong>{tProfile('fields.analysisDate')}:</strong>{' '}
                      {prettyTime(profile.skin.aiAnalysis.analysisDate)}
                    </p>
                  )}
                  {profile.skin.aiAnalysis.recommendations && profile.skin.aiAnalysis.recommendations.length > 0 && (
                    <div>
                      <strong>{tProfile('fields.recommendations')}:</strong>
                      <ul className="profile-card__ai-recommendations">
                        {profile.skin.aiAnalysis.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {showReportCard && profile.skin && (
          <SkinReportCard
            skin={profile.skin}
            userName={profile.identity?.name ?? undefined}
            onClose={() => setShowReportCard(false)}
          />
        )}

        {/* Current Routine Card */}
        <div className={`profile-card ${!hasCurrentRoutine ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('skin.currentProducts', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">🧴</span>
            <span className="profile-card__title">{tProfile('currentRoutine')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'currentProducts' ? (
              <div className="profile-card__edit">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveArrayField('currentProducts', 'skin', editValue);
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  placeholder={tProfile('placeholders.separateWithCommasExample')}
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button type="button" className="btn btn--sm btn--primary" onClick={() => handleSaveArrayField('currentProducts', 'skin', editValue)}>
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="profile-card__value--editable"
                onClick={() => handleStartEdit('currentProducts', profile.skin?.currentProducts?.join(', ') || '')}
              >
                {profile.skin?.currentProducts && profile.skin.currentProducts.length > 0 ? (
                  <div className="profile-card__list">
                    {profile.skin.currentProducts.map((product, idx) => (
                      <span key={idx} className="profile-card__list-item">
                        {product}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="profile-card__empty">
                    {tProfile('placeholders.tellMeCurrentProducts')}
                  </span>
                )}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Preferred Brands Card */}
        <div className={`profile-card ${!hasBrands ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('skin.preferredBrands', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">❤️</span>
            <span className="profile-card__title">{tProfile('preferredBrands')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'preferredBrands' ? (
              <div className="profile-card__edit">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveArrayField('preferredBrands', 'skin', editValue);
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  placeholder={tProfile('placeholders.separateWithCommas')}
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button 
                    type="button" 
                    className="btn btn--sm btn--primary" 
                    onClick={() => handleSaveArrayField('preferredBrands', 'skin', editValue)}
                  >
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-card__tags profile-card__value--editable"
                onClick={() => handleStartEdit('preferredBrands', (profile.skin?.preferredBrands || []).join(', '))}
              >
                {profile.skin?.preferredBrands && profile.skin.preferredBrands.length > 0 ? (
                  profile.skin.preferredBrands.map((brand, idx) => (
                    <span key={idx} className="profile-card__tag">
                      {brand}
                    </span>
                  ))
                ) : (
                  <span className="profile-card__empty">
                    {tProfile('placeholders.tellMePreferredBrands')}
                  </span>
                )}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Routine Goals Card */}
        <div className={`profile-card ${!hasGoals ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('skin.routineGoals', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">🎯</span>
            <span className="profile-card__title">{tProfile('skincareGoals')}</span>
          </div>
          <div className="profile-card__content">
            {editingField === 'routineGoals' ? (
              <div className="profile-card__edit">
                {/* Preset goal chips — i18n labels, stored as English values */}
                <div className="profile-card__presets" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  {(['clearAcne', 'reduceWrinkles', 'evenSkinTone', 'glassSkin', 'hydration', 'antiAging', 'poreCare', 'brightenSkin'] as const).map((key) => {
                    const enValue = { clearAcne: 'Clear acne', reduceWrinkles: 'Reduce wrinkles', evenSkinTone: 'Even skin tone', glassSkin: 'Glass skin', hydration: 'Hydration', antiAging: 'Anti-aging', poreCare: 'Pore care', brightenSkin: 'Brighten skin' }[key];
                    const currentGoals = editValue.split(',').map(g => g.trim()).filter(Boolean);
                    const isSelected = currentGoals.some(g => g.toLowerCase() === enValue.toLowerCase());
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`profile-card__preset-chip ${isSelected ? 'profile-card__preset-chip--active' : ''}`}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '14px',
                          border: isSelected ? '1.5px solid var(--color-primary, #8b5cf6)' : '1px solid var(--border-color, #d1d5db)',
                          background: isSelected ? 'var(--color-primary-bg, rgba(139, 92, 246, 0.1))' : 'transparent',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: isSelected ? 'var(--color-primary, #8b5cf6)' : 'inherit',
                        }}
                        onClick={() => {
                          if (isSelected) {
                            setEditValue(currentGoals.filter(g => g.toLowerCase() !== enValue.toLowerCase()).join(', '));
                          } else {
                            setEditValue([...currentGoals, enValue].join(', '));
                          }
                        }}
                      >
                        {tProfile(`goalPresets.${key}`)}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  value={editValue}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveArrayField('routineGoals', 'skin', editValue);
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  placeholder={tProfile('placeholders.separateWithCommas')}
                  autoFocus
                  className="profile-card__input"
                />
                <div className="profile-card__edit-actions">
                  <button 
                    type="button" 
                    className="btn btn--sm btn--primary" 
                    onClick={() => handleSaveArrayField('routineGoals', 'skin', editValue)}
                  >
                    ✓
                  </button>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={handleCancelEdit}>
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="profile-card__tags profile-card__value--editable"
                onClick={() => handleStartEdit('routineGoals', (profile.skin?.routineGoals || []).join(', '))}
              >
                {profile.skin?.routineGoals && profile.skin.routineGoals.length > 0 ? (
                  profile.skin.routineGoals.map((goal, idx) => (
                    <span key={idx} className="profile-card__tag">
                      {goal}
                    </span>
                  ))
                ) : (
                  <span className="profile-card__empty">
                    {tProfile('placeholders.tellMeSkincareGoals')}
                  </span>
                )}
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            )}
          </div>
        </div>

        {/* Delivery Address Card - only show when in-house selling or sample features are active */}
        {(INHOUSE_SELLING_ENABLED || SAMPLE_FEATURE_ENABLED) && (
        <div className={`profile-card ${!hasAddress ? 'profile-card--empty' : ''}`}>
          {isRecentlyUpdated('logistics', recentChanges, diff) && <UpdatedBadge />}
          <div className="profile-card__header">
            <span className="profile-card__icon">📍</span>
            <span className="profile-card__title">{tProfile('deliveryAddress')}</span>
          </div>
          <div className="profile-card__content">
            {profile.logistics?.address1 ? (
              <div 
                className="profile-card__address profile-card__value--editable"
                onClick={() => setIsAddressFormOpen(true)}
              >
                <p>
                  {[profile.logistics.address1, profile.logistics.address2, profile.logistics.city, profile.logistics.state, profile.logistics.postalCode, profile.logistics.country]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                <span className="profile-card__edit-icon">✏️</span>
              </div>
            ) : (
              <button 
                type="button"
                className="btn btn--sm btn--secondary"
                onClick={() => setIsAddressFormOpen(true)}
              >
                {tProfile('actions.addAddress')}
              </button>
            )}
            {/* Sample Request Status Badge - small, under address with tooltip */}
            {SAMPLE_FEATURE_ENABLED && (profile.segment?.customerStage === 'sample-requested' || 
              profile.segment?.customerStage === 'sample-sent' ||
              profile.segment?.customerStage === 'sample-delivered') && (
              <div 
                className={`sample-status-badge sample-status-badge--${profile.segment.customerStage}`}
                data-tooltip={
                  profile.segment.customerStage === 'sample-requested'
                    ? tProfile('sampleStatus.requestedTooltip')
                    : profile.segment.customerStage === 'sample-sent'
                      ? tProfile('sampleStatus.shippedTooltip')
                      : tProfile('sampleStatus.deliveredTooltip')
                }
              >
                <span className="sample-status-badge__icon">
                  {profile.segment.customerStage === 'sample-requested' ? '📦' : 
                   profile.segment.customerStage === 'sample-sent' ? '🚚' : '✅'}
                </span>
                <span className="sample-status-badge__text">
                  {profile.segment.customerStage === 'sample-requested' 
                    ? tProfile('sampleStatus.received')
                    : profile.segment.customerStage === 'sample-sent' 
                      ? tProfile('sampleStatus.shipped')
                      : tProfile('sampleStatus.delivered')}
                </span>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Empty State */}
      {completion === 0 && (
        <div className="profile-panel__empty-state">
          <p>
            {tProfile('emptyState')}
          </p>
        </div>
      )}

      {/* Diff/Changes Section - Hidden since we now show NEW badges inline on cards */}
      {/* Keeping code for potential future use but not rendering */}
      {false && recentChanges && recentChanges.length > 0 && (
        <section className="profile-panel__section profile-panel__diff">
          <h3>{tProfile('recentChanges')}</h3>
          <ul className="profile-panel__changes-list">
            {recentChanges.map((change, index) => (
              <li key={`${change.path}-${index}`} className="profile-panel__change-item">
                <span className="profile-panel__change-path">{change.path}</span>
                <span className="profile-panel__change-arrow">→</span>
                <span className="profile-panel__change-value">{renderJson(change.newVal)}</span>
                {onUndo && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => onUndo(change.path)}
                    title={t('actions.undo')}
                  >
                    ↩️
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Conflicts Section - Only show if there are meaningful conflicts (not null/null) */}
      {(() => {
        const meaningfulConflicts = (conflicts || []).filter(c => {
          const existing = c?.stored ?? c?.existing;
          const incoming = c?.incoming ?? c?.proposed;
          // Filter out conflicts where both values are null/undefined
          return existing != null || incoming != null;
        });
        if (meaningfulConflicts.length === 0) return null;
        return (
          <section className="profile-panel__section profile-panel__conflicts">
            <h3>⚠️ {tProfile('conflictsDetected')}</h3>
            <ul className="profile-panel__conflicts-list">
              {meaningfulConflicts.map((conflict, index) => (
                <li key={`${conflict?.path ?? 'conflict'}-${index}`}>
                  <p>
                    <strong>{conflict?.path}</strong>
                  </p>
                  <p>Existing: {JSON.stringify(conflict?.stored ?? conflict?.existing ?? null)}</p>
                  <p>Incoming: {JSON.stringify(conflict?.incoming ?? conflict?.proposed ?? null)}</p>
                  {conflict?.confidence != null && (
                    <p>Confidence: {Math.round(Number(conflict.confidence) * 100)}%</p>
                  )}
                  {conflict?.evidence && <p className="profile-panel__evidence">Evidence: {conflict.evidence}</p>}
                </li>
              ))}
            </ul>
          </section>
        );
      })()}

      {/* Phase 2: Show Editor Picks (Sponsored Recommendations) */}
      {(() => {
        const isPhase2 = completion >= 50;
        
        if (!isPhase2) return null;
        
        return (
          <section className="profile-panel__section">
            <div className="profile-panel__section-header">
              <h3>{isEditorPicksAffiliate ? tProfile('editorPicks.shopNow') : tProfile('editorPicks.title')}</h3>
              <button 
                type="button" 
                className="btn btn--ghost btn--sm"
                onClick={() => onRefreshEditorPicks()} 
                disabled={isEditorPicksLoading}
              >
                {tProfile('editorPicks.refresh')}
              </button>
            </div>
            {isEditorPicksLoading && <p className="profile-panel__loading">{tProfile('editorPicks.loading')}</p>}
            {!isEditorPicksLoading && editorPicks?.length === 0 && <p className="profile-panel__empty">{tProfile('editorPicks.empty')}</p>}
            <div className="editor-picks-grid">
              {editorPicks?.map((pick, index) => (
                <article key={`${pick.id}-${index}`} className="product-card">
                  {/* Badge: Show affiliate source + ad label for affiliate, SPONSORED for paid, EDITOR'S PICK for organic */}
                  {isEditorPicksAffiliate || pick.affiliateSource ? (
                    <div className="product-card__badge product-card__badge--sponsored">
                      {getAffiliateDisplayName(pick.affiliateSource, pick.brand)} {tProfile('editorPicks.adBadge')}
                    </div>
                  ) : pick.isPaidPlacement ? (
                    <div className="product-card__badge product-card__badge--sponsored">{tProfile('editorPicks.sponsored')}</div>
                  ) : (
                    <div className="product-card__badge">{tProfile('editorPicks.editorsPick')}</div>
                  )}
                  
                  {/* Product Image */}
                  <div className="product-card__image-wrapper">
                    {pick.image ? (
                      <img 
                        src={pick.image} 
                        alt={pick.name ?? 'Product'}
                        className="product-card__image"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const nextSibling = target.nextSibling as HTMLElement;
                          if (nextSibling) {
                            nextSibling.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="product-card__image-placeholder" style={{ display: pick.image ? 'none' : 'flex' }}>
                      🧴
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="product-card__content">
                    <h4 className="product-card__title">{cleanProductTitle(pick.name ?? pick.id)}</h4>
                    
                    <div className="product-card__meta">
                      {/* Hide brand for affiliate products - badge and button already show the source */}
                      {pick.brand && !isEditorPicksAffiliate && !pick.affiliateSource && (
                        <span className="product-card__brand">{pick.brand}</span>
                      )}
                      {pick.price ? (
                        <>
                          <span className="product-card__separator">·</span>
                          <span className="product-card__price">${Number(pick.price).toFixed(2)}</span>
                        </>
                      ) : (isEditorPicksAffiliate || pick.affiliateSource) && pick.url ? (
                        <>
                          <span className="product-card__separator">·</span>
                          <a href={pick.affiliateUrl || pick.url} target="_blank" rel="noopener noreferrer" className="product-card__price product-card__price--link">
                            {tProfile('editorPicks.seePrice')}
                          </a>
                        </>
                      ) : null}
                    </div>

                    {/* Match Score */}
                    {pick.confidence != null && (
                      <div className="product-card__match">
                        <span className="product-card__match-icon">⭐</span>
                        <span className="product-card__match-score">{Math.round(Number(pick.confidence) * 100)}% Match</span>
                      </div>
                    )}

                    {/* Reason - Hide for affiliate products as title already describes the product */}
                    {(pick.reason || pick.summary) && !isEditorPicksAffiliate && !pick.affiliateSource && (
                      <p className="product-card__reason">{pick.reason ?? pick.summary}</p>
                    )}

                    {/* Tags */}
                    {(pick.tags?.length ?? 0) > 0 || (pick.reasons?.length ?? 0) > 0 ? (
                      <div className="product-card__tags">
                        {(pick.tags ?? pick.reasons ?? []).slice(0, 4).map((tag, idx) => (
                          <span key={`${pick.id}-tag-${idx}`} className="product-card__tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {/* Actions - Affiliate mode shows only external link, inhouse shows cart/wishlist */}
                    <div className="product-card__actions">
                      {(isEditorPicksAffiliate || pick.affiliateSource) ? (
                        /* Affiliate mode: Shop on [Source] + wishlist + why this */
                        <>
                        <a 
                          href={pick.affiliateUrl || pick.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="product-card__button product-card__button--primary"
                          onClick={() => {
                            // Track affiliate click for analytics
                            if (user?.uid) {
                              const trackablePick = {
                                ...pick,
                                price: typeof pick.price === 'string' ? parseFloat(pick.price) : pick.price
                              };
                              trackEditorPickClicked(user.uid, trackablePick).catch((err: Error) =>
                                console.warn('⚠️ Failed to track click:', err)
                              );
                              // Geo-routing analytics: track with region + marketplace
                              const region = detectUserRegion(masterProfile?.identity?.country);
                              const rawPrice = typeof pick.price === 'string' ? parseFloat(pick.price) : pick.price;
                              const safePrice = typeof rawPrice === 'number' && Number.isFinite(rawPrice) ? rawPrice : undefined;
                              trackAffiliateClick(user.uid, {
                                productId: pick.id,
                                productName: pick.name || pick.title,
                                brand: pick.brand,
                                price: safePrice,
                                url: pick.affiliateUrl || pick.url,
                                affiliateSource: pick.affiliateSource,
                                region,
                                isPaidPlacement: pick.isPaidPlacement,
                                placementRow: 'ai-picks',
                              }).catch(() => {});
                            }
                          }}
                        >
                          Shop on {getAffiliateDisplayName(pick.affiliateSource, pick.brand)} →
                        </a>
                        {onWishlistToggle && (
                          <button 
                            className={`product-card__button product-card__button--secondary ${isInWishlist?.(pick.id) ? 'in-wishlist' : ''}`}
                            onClick={() => onWishlistToggle(pick)}
                            title={isInWishlist?.(pick.id) ? t('product.inMyPicks') : t('product.saveToMyPicks')}
                          >
                            {isInWishlist?.(pick.id) ? '❤️' : '♡'}
                          </button>
                        )}
                        {onAskAI && (pick.name || pick.title) && (
                          <button
                            className="product-card__button product-card__button--secondary"
                            onClick={() => onAskAI(pick.name || pick.title || '')}
                            title={tProfile('editorPicks.whyThis')}
                          >
                            💬
                          </button>
                        )}
                        </>
                      ) : (
                        /* Inhouse mode: View Product + Cart + Wishlist buttons */
                        <>
                          {pick.url && (
                            <button
                              type="button"
                              className="product-card__button product-card__button--primary"
                              onClick={() => {
                                // Track editor pick click for conversion analytics
                                if (user?.uid) {
                                  const trackablePick = {
                                    ...pick,
                                    price: typeof pick.price === 'string' ? parseFloat(pick.price) : pick.price
                                  };
                                  trackEditorPickClicked(user.uid, trackablePick).catch((err: Error) =>
                                    console.warn('⚠️ Failed to track click:', err)
                                  );
                                }
                                if (onViewDetails) {
                                  onViewDetails(pick);
                                } else if (pick.url) {
                                  openProductUrl(pick.url);
                                }
                              }}
                            >
                              View Product →
                            </button>
                          )}
                          {INHOUSE_SELLING_ENABLED && (
                            <button 
                              className="product-card__button product-card__button--primary"
                              onClick={() => {
                                onAddToCart({
                                  id: pick.id,
                                  name: pick.name || pick.title || 'Product',
                                  price: pick.price,
                                  priceUSD: parseFloat(String(pick.price)) || 0,
                                  image: pick.image,
                                  brand: pick.brand,
                                });
                              }}
                              style={{ marginLeft: '8px' }}
                            >
                              {t('product.addShort')}
                            </button>
                          )}
                          {onWishlistToggle && (
                            <button 
                              className={`product-card__button product-card__button--secondary ${isInWishlist?.(pick.id) ? 'in-wishlist' : ''}`}
                              onClick={() => {
                                if (onWishlistToggle) {
                                  onWishlistToggle(pick);
                                }
                              }}
                              title={isInWishlist?.(pick.id) ? t('product.inMyPicks') : t('product.saveToMyPicks')}
                            >
                              {isInWishlist?.(pick.id) ? '❤️' : '♡'}
                            </button>
                          )}
                          {onAskAI && (pick.name || pick.title) && (
                            <button
                              className="product-card__button product-card__button--secondary"
                              onClick={() => onAskAI(pick.name || pick.title || '')}
                              title={tProfile('editorPicks.whyThis')}
                            >
                              💬
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        );
      })()}

      {/* Address Form Modal - Rendered via Portal to escape ProfilePanel's overflow: hidden */}
      {(isAddressFormOpen || showAddressFormForSample) && ReactDOM.createPortal(
        <AddressForm
          isOpen={true}
          initialAddress={(profile.logistics || {}) as Partial<import('../checkout/AddressForm').AddressData>}
          onSave={handleSaveAddress}
          onCancel={handleCancelAddressForm}
          language={language}
          forSampleRequest={showAddressFormForSample}
        />,
        document.body
      )}
    </aside>
  );
};

export default ProfilePanel;
